/**
 * Interacción de las tarjetas de "Productos destacados".
 *
 * Todo va por delegación en `document` y no por listeners atados a cada
 * tarjeta a propósito: en la ficha de producto esta sección se usa como
 * recomendaciones, y Horizon la sustituye entera por la respuesta del fetch
 * (morphSection). Un <script> inline no se reejecuta tras ese reemplazo y los
 * selectores de peso y el carrusel se quedarían muertos. Delegando, el HTML
 * nuevo funciona sin volver a inicializar nada.
 */
(() => {
  const PASO_EXTRA = 20;               // el gap del carrusel, en px

  const enteroDe = (input) => Math.max(1, parseInt(input.value, 10) || 1);

  const ajustarCantidad = (caja, delta) => {
    const input = caja.querySelector('input[name="quantity"]');
    if (!input) return;
    input.value = enteroDe(input) + delta;
    const menos = caja.querySelector('[data-pd-menos]');
    if (menos) menos.disabled = enteroDe(input) <= 1;
  };

  const pasoCarrusel = (pista) => {
    const card = pista.querySelector('.pd-card');
    return card ? card.getBoundingClientRect().width + PASO_EXTRA : 260;
  };

  const refrescarFlechas = (pista) => {
    const cabecera = pista.closest('.pd-inner')?.querySelector('.pd-nav');
    if (!cabecera) return;
    const prev = cabecera.querySelector('[data-pd-prev]');
    const next = cabecera.querySelector('[data-pd-next]');
    const tope = pista.scrollWidth - pista.clientWidth - 2;
    if (prev) prev.disabled = pista.scrollLeft <= 2;
    if (next) next.disabled = pista.scrollLeft >= tope;
  };

  const pistaDe = (boton) => boton.closest('.pd-inner')?.querySelector('[data-pd-track]');

  document.addEventListener('click', (e) => {
    const menos = e.target.closest('[data-pd-menos]');
    if (menos) return ajustarCantidad(menos.closest('.pd-qty'), -1);

    const mas = e.target.closest('[data-pd-mas]');
    if (mas) return ajustarCantidad(mas.closest('.pd-qty'), 1);

    const prev = e.target.closest('[data-pd-prev]');
    if (prev) {
      const pista = pistaDe(prev);
      if (pista) pista.scrollBy({ left: -pasoCarrusel(pista), behavior: 'smooth' });
      return;
    }
    const next = e.target.closest('[data-pd-next]');
    if (next) {
      const pista = pistaDe(next);
      if (pista) pista.scrollBy({ left: pasoCarrusel(pista), behavior: 'smooth' });
    }
  });

  // El peso elegido cambia el precio que se ve y la variante que va al carrito.
  document.addEventListener('change', (e) => {
    const input = e.target;
    if (!input.classList?.contains('pd-peso__input')) return;
    const card = input.closest('.pd-card');
    if (!card) return;

    const oculto = card.querySelector('input[ref="variantId"]');
    if (oculto) {
      oculto.value = input.value;
      oculto.disabled = false;
    }

    const precio = card.querySelector('.pd-price');
    if (precio && input.dataset.precio) precio.textContent = input.dataset.precio;

    // Precio anterior y porcentaje: sólo si ESTE peso está rebajado.
    const dto = input.dataset.dto || '';
    const antes = card.querySelector('.pd-antes');
    const valor = card.querySelector('.pd-antes__val');
    const badge = card.querySelector('.pd-dto');
    if (precio) precio.classList.toggle('pd-price--oferta', dto !== '');
    if (antes && valor) {
      valor.textContent = input.dataset.antes || '';
      antes.hidden = dto === '';
    }
    if (badge) {
      badge.textContent = `−${dto}%`;
      badge.setAttribute('aria-label', `${dto} % de descuento`);
      badge.hidden = dto === '';
    }
  });

  // El scroll no burbujea: se escucha en fase de captura para no atar un
  // listener por pista.
  document.addEventListener('scroll', (e) => {
    const pista = e.target;
    if (pista instanceof Element && pista.hasAttribute?.('data-pd-track')) refrescarFlechas(pista);
  }, { capture: true, passive: true });

  const repasarTodo = () => {
    document.querySelectorAll('[data-pd-track]').forEach(refrescarFlechas);
    document.querySelectorAll('.pd-qty').forEach((q) => ajustarCantidad(q, 0));
  };

  window.addEventListener('resize', repasarTodo);

  // Tras el reemplazo de la sección hay que recalcular el estado de las
  // flechas. Se agrupan las mutaciones en un frame para no repasar el DOM en
  // cada una, y sólo se atiende a las que traen una pista nueva.
  let pendiente = false;
  new MutationObserver((mutaciones) => {
    if (pendiente) return;
    const relevante = mutaciones.some((m) => [...m.addedNodes].some(
      (n) => n.nodeType === 1 && (n.matches?.('[data-pd-track]') || n.querySelector?.('[data-pd-track]'))
    ));
    if (!relevante) return;
    pendiente = true;
    requestAnimationFrame(() => { pendiente = false; repasarTodo(); });
  }).observe(document.body, { childList: true, subtree: true });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repasarTodo);
  } else {
    repasarTodo();
  }
})();
